/**
 * Forum Topic Page - Store
 * 
 * Modern topic page with posts, reactions, and enhanced UX
 */

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStore } from '@/contexts/StoreContext'
import { useAvatar } from '@/hooks/useAvatar'
import { useTheme } from '@/contexts/ThemeContext'
import { usePlayerAuth } from '@/contexts/PlayerAuthContext'
import forumStoreService, {
  type ForumTopic,
  type ForumPost,
} from '@/lib/services/forumStore.service'
import forumModService from '@/lib/services/forumMod.service'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AnimatedBadge, { BadgeStyle, BadgeAnimation } from '@/components/common/AnimatedBadge'
import {
  ArrowLeft,
  Pin,
  Lock,
  MessageCircle,
  Heart,
  Smile,
  ThumbsUp,
  Frown,
  AlertCircle,
  Send,
  Eye,
  Clock,
  LogIn,
  Share2,
  Users,
  Pencil,
  Trash2,
  ShieldAlert,
} from 'lucide-react'
import PageHero from '@/components/common/PageHero'
import { PostReactions } from '@/components/forum/PostReactions'
import { TopicModTools } from '@/components/forum/TopicModTools'
import { PostModTools } from '@/components/forum/PostModTools'
import { TopicAuthorActions } from '@/components/forum/TopicAuthorActions'
import { EditPostModal } from '@/components/forum/EditPostModal'
import { MoveTopicModal } from '@/components/forum/MoveTopicModal'
import { DeleteConfirmModal } from '@/components/forum/DeleteConfirmModal'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from 'react-i18next';

import { Caixa } from '@/construtor/caixa';
import { EspacoDeAnuncio } from '@/templates/comum/anuncios/EspacoDeAnuncio';
export default function ForumTopicPage() {
  const { t } = useTranslation();
  const { store } = useStore()
  const { config } = useTheme()
  const { isAuthenticated, player } = usePlayerAuth()
  const { getAvatarUrl } = useAvatar()
  const { categorySlug, boardSlug, topicSlug } = useParams<{
    categorySlug: string
    boardSlug: string
    topicSlug: string
  }>()
  const navigate = useNavigate()
  const { openLoginModal } = useAuth()

  const [topic, setTopic] = useState<ForumTopic | null>(null)
  const [posts, setPosts] = useState<ForumPost[]>([])
  const [loading, setLoading] = useState(true)
  const [replyContent, setReplyContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const viewIncrementedRef = React.useRef(false)
  const currentTopicRef = React.useRef('')
  const isLoadingRef = React.useRef(false)

  // Modal states
  const [editPostModalOpen, setEditPostModalOpen] = useState(false)
  const [moveTopicModalOpen, setMoveTopicModalOpen] = useState(false)
  const [deleteTopicModalOpen, setDeleteTopicModalOpen] = useState(false)
  const [deletePostModalOpen, setDeletePostModalOpen] = useState(false)
  const [selectedPost, setSelectedPost] = useState<ForumPost | null>(null)
  const [postToDelete, setPostToDelete] = useState<string | null>(null)

  useEffect(() => {
    const topicKey = `${categorySlug}_${boardSlug}_${topicSlug}`
    
    // Reset ref if topic changed
    if (currentTopicRef.current !== topicKey) {
      viewIncrementedRef.current = false
      currentTopicRef.current = topicKey
      isLoadingRef.current = false
    }
    
    if (categorySlug && boardSlug && topicSlug && store?.id) {
      loadTopic()
    }
  }, [categorySlug, boardSlug, topicSlug, store])

  async function loadTopic(isReload = false) {
    if (!store?.id) return
    
    // Prevent concurrent calls
    if (isLoadingRef.current) return
    isLoadingRef.current = true
    
    try {
      setLoading(true)
      
      // Check and set flag synchronously to prevent race conditions
      const shouldIncrementView = !viewIncrementedRef.current
      
      // Set flag IMMEDIATELY before async call (prevents double increment)
      if (shouldIncrementView) {
        viewIncrementedRef.current = true
      }
      
      // Fetch topic data
      const topicData = await forumStoreService.getTopicBySlug(
        store.id,
        categorySlug!,
        boardSlug!,
        topicSlug!,
        !shouldIncrementView // Skip if already incremented
      )
      setTopic(topicData)

      const postsData = await forumStoreService.listPosts(store!.id, topicData.id)
      setPosts(postsData.posts)
    } catch (error) {
      console.error('❌ Erro ao carregar tópico:', error)
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }

  async function handleReply(e: React.FormEvent) {
    e.preventDefault()

    if (!replyContent.trim() || !topic) return

    try {
      setSubmitting(true)
      await forumStoreService.createPost(store!.id, {
        topicId: topic.id,
        content: replyContent,
      })
      setReplyContent('')
      // Reload posts without re-fetching topic (avoid double increment)
      if (topic) {
        const postsData = await forumStoreService.listPosts(store!.id, topic.id)
        setPosts(postsData.posts)
      }
    } catch (error) {
      console.error('❌ Erro ao criar resposta:', error)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleReact(postId: string, type: string) {
    try {
      await forumStoreService.reactToPost(store!.id, postId, type)
      // Reload posts to get updated reactions
      if (topic) {
        const postsData = await forumStoreService.listPosts(store!.id, topic.id)
        setPosts(postsData.posts)
      }
    } catch (error) {
      console.error('❌ Erro ao reagir:', error)
    }
  }

  async function handleRemoveReaction(postId: string) {
    try {
      await forumStoreService.removeReaction(store!.id, postId)
      // Reload posts to get updated reactions
      if (topic) {
        const postsData = await forumStoreService.listPosts(store!.id, topic.id)
        setPosts(postsData.posts)
      }
    } catch (error) {
      console.error('❌ Erro ao remover reação:', error)
    }
  }

  // Moderation handlers
  async function handleLockTopic() {
    if (!topic) return
    try {
      const result = await forumModService.lockTopic(store!.id, topic.id)
      toast.success(result.message)
      // Reload topic to update lock status
      await loadTopic(true)
    } catch (error: any) {
      toast.error(error.message || 'Erro ao trancar tópico')
    }
  }

  async function handleUnlockTopic() {
    if (!topic) return
    try {
      const result = await forumModService.unlockTopic(store!.id, topic.id)
      toast.success(result.message)
      // Reload topic to update lock status
      await loadTopic(true)
    } catch (error: any) {
      toast.error(error.message || 'Erro ao destrancar tópico')
    }
  }

  async function handlePinTopic() {
    if (!topic) return
    try {
      const result = await forumModService.pinTopic(store!.id, topic.id)
      toast.success(result.message)
      // Reload topic to update pin status
      await loadTopic(true)
    } catch (error: any) {
      toast.error(error.message || 'Erro ao fixar tópico')
    }
  }

  async function handleUnpinTopic() {
    if (!topic) return
    try {
      const result = await forumModService.unpinTopic(store!.id, topic.id)
      toast.success(result.message)
      // Reload topic to update pin status
      await loadTopic(true)
    } catch (error: any) {
      toast.error(error.message || 'Erro ao desafixar tópico')
    }
  }

  function handleDeleteTopic() {
    setDeleteTopicModalOpen(true)
  }

  async function confirmDeleteTopic() {
    if (!topic) return
    
    try {
      const result = await forumModService.deleteTopic(store!.id, topic.id)
      toast.success(result.message)
      // Navigate back to board
      navigate(`/forum/${categorySlug}/${boardSlug}`)
    } catch (error: any) {
      toast.error(error.message || 'Erro ao deletar tópico')
      throw error
    }
  }

  async function handleMoveTopic() {
    setMoveTopicModalOpen(true)
  }

  async function handleMoveTopicConfirm(targetBoardId: string) {
    if (!topic) return
    try {
      const result = await forumModService.moveTopic(store!.id, topic.id, targetBoardId)
      toast.success(result.message)
      // Redirect to new location with full page reload to ensure permissions are loaded
      const newUrl = `/forum/${result.topic.categorySlug}/${result.topic.boardSlug}/${result.topic.slug}`
      window.location.href = newUrl
    } catch (error: any) {
      toast.error(error.message || 'Erro ao mover tópico')
      throw error
    }
  }

  function handleEditPost(postId: string) {
    const post = posts.find(p => p.id === postId)
    if (post) {
      setSelectedPost(post)
      setEditPostModalOpen(true)
    }
  }

  async function handleEditPostSave(content: string) {
    if (!selectedPost) return
    try {
      const result = await forumModService.updatePost(store!.id, selectedPost.id, content)
      toast.success(result.message)
      // Reload posts
      if (topic) {
        const postsData = await forumStoreService.listPosts(store!.id, topic.id)
        setPosts(postsData.posts)
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao editar post')
      throw error
    }
  }

  function handleDeletePost(postId: string) {
    setPostToDelete(postId)
    setDeletePostModalOpen(true)
  }

  async function confirmDeletePost() {
    if (!postToDelete) return
    
    try {
      console.log('🗑️ Deletando post:', postToDelete)
      const result = await forumModService.deletePost(store!.id, postToDelete)
      console.log('✅ Post deletado, resultado:', result)
      toast.success(result.message)
      
      // Force reload posts to show deleted state
      if (topic) {
        console.log('🔄 Recarregando posts do tópico:', topic.id)
        const postsData = await forumStoreService.listPosts(store!.id, topic.id)
        console.log('📝 Posts recarregados:', postsData.posts.length, 'posts')
        console.log('🔍 Posts conteúdo:', postsData.posts.map(p => ({ id: p.id, content: p.content.substring(0, 50) })))
        setPosts(postsData.posts)
      }
    } catch (error: any) {
      console.error('❌ Erro ao deletar post:', error)
      toast.error(error.message || 'Erro ao deletar post')
      throw error
    } finally {
      setPostToDelete(null)
    }
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  function isPostDeletedByModerator(content: string): boolean {
    const isDeleted = content.startsWith('[DELETED_BY_MODERATOR]')
    if (isDeleted) {
      console.log('🚨 Post deletado detectado:', content.substring(0, 100))
    }
    return isDeleted
  }

  function getModeratorNameFromDeletedPost(content: string): string {
    if (!isPostDeletedByModerator(content)) return ''
    const modName = content.replace('[DELETED_BY_MODERATOR]', '')
    console.log('👤 Nome do moderador extraído:', modName)
    return modName
  }

  function formatRelativeTime(dateString: string) {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (hours < 1) return 'Agora'
    if (hours < 24) return `${hours}h atrás`
    if (days === 1) return 'Ontem'
    if (days < 7) return `${days} dias atrás`
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  if (loading) {
    return (
      <div>
        <PageHero
          title="Fórum"
          breadcrumbs={[
            { label: 'Fórum', href: '/forum' },
            { label: t('store.forum.loading') },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!topic) {
    return (
      <div>
        <PageHero
          title="Tópico não encontrado"
          breadcrumbs={[
            { label: 'Fórum', href: '/forum' },
            { label: 'Erro' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card className="border-card-border">
            <CardContent className="p-12">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-destructive/20 to-destructive/5 flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-destructive" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {t('store.forum.topicNotFound')}
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    O tópico que você está procurando não existe ou foi removido.
                  </p>
                </div>
                <Button onClick={() => navigate('/forum')} className="mt-4">
                  {t('store.forum.backToForum')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Hero */}
      <PageHero
        title={topic.title}
        breadcrumbs={[
          { label: 'Fórum', href: '/forum' },
          { label: topic.title },
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 max-w-7xl -mt-24 pt-24 relative z-10">
        {/* Topic Header with Moderation Tools */}
        <Caixa id="forum.topico.cabecalho">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {topic.isPinned && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                <Pin className="w-3 h-3 mr-1" />
                {t('store.forum.pinned')}
              </Badge>
            )}
            {topic.isLocked && (
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
                <Lock className="w-3 h-3 mr-1" />
                {t('store.forum.locked')}
              </Badge>
            )}
          </div>
          <TopicModTools
            topicId={topic.id}
            isLocked={topic.isLocked}
            isPinned={topic.isPinned}
            boardId={topic.boardId}
            onLock={handleLockTopic}
            onUnlock={handleUnlockTopic}
            onPin={handlePinTopic}
            onUnpin={handleUnpinTopic}
            onDelete={handleDeleteTopic}
            onMove={handleMoveTopic}
          />
        </div>
        </Caixa>

        <div className="space-y-6">
            {posts.length === 0 ? (
              <Caixa id="forum.topico.vazio" miolo>
              <Card className="border-card-border">
                <CardContent className="p-12">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <MessageCircle className="w-10 h-10 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-2">
                        {t('store.forum.emptyRepliesTitle')}
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        {t('store.forum.emptyRepliesBody')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Caixa>
            ) : (
              <>
                {/* Original Topic Post */}
                {posts.length > 0 && (
                  <Caixa id="forum.topico.principal" miolo key={posts[0].id}>
                  <Card
                    key={posts[0].id}
                    className="border-2 border-primary/30 hover:border-primary/50 transition-all overflow-hidden shadow-xl bg-gradient-to-br from-primary/5 via-accent/5 to-background"
                  >
                    <CardContent className="p-0">
                      {/* Grid Layout: Author Sidebar + Content */}
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                        
                        {/* Left Sidebar - Author Info */}
                        <div className="lg:col-span-3 bg-background-secondary border-r border-card-border p-6 flex flex-col">
                          {/* Avatar */}
                          <div className="flex justify-center mb-4">
                            <div className="w-20 h-20 rounded-xl overflow-hidden shadow-lg border-2 border-card-border">
                              <img
                                src={getAvatarUrl(posts[0].authorHytaleNick || posts[0].authorName, 128)}
                                alt={posts[0].authorName || 'Jogador'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                  target.parentElement!.classList.add('bg-gradient-to-br', 'from-primary', 'via-accent', 'to-primary', 'flex', 'items-center', 'justify-center', 'text-primary-foreground', 'font-bold', 'text-3xl')
                                  target.parentElement!.textContent = posts[0].authorName ? posts[0].authorName[0].toUpperCase() : '?'
                                }}
                              />
                            </div>
                          </div>

                          {/* Author Name */}
                          <div className="text-center mb-4">
                            <h3 className="text-lg font-bold text-foreground mb-1">
                              {posts[0].authorName || 'Jogador'}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              @{posts[0].authorHytaleNick || 'jogador'}
                            </p>
                            {posts[0].authorRole && (
                              <p className="text-xs text-primary font-semibold mt-1">
                                {posts[0].authorRole}
                              </p>
                            )}
                            <Badge 
                              variant="default" 
                              className="bg-primary/20 text-primary border-primary/40 text-xs px-3 py-1 mt-2"
                            >
                              {t('store.forum.topicAuthor')}
                            </Badge>
                          </div>

                          {/* Badges */}
                          {posts[0].authorBadges.length > 0 && (
                            <div className="flex flex-wrap gap-2 justify-center mb-4 pb-4 border-b border-card-border">
                              {posts[0].authorBadges.map((badge) => (
                                <AnimatedBadge
                                  key={badge.id}
                                  name={badge.name}
                                  icon={badge.icon}
                                  style={(badge.style as BadgeStyle) || 'DEFAULT'}
                                  animation={(badge.animation as BadgeAnimation) || 'NONE'}
                                  className="text-xs"
                                />
                              ))}
                            </div>
                          )}

                          {/* Post Meta */}
                          <div className="mt-auto space-y-2">
                            <div className="flex items-center justify-between text-xs text-muted-foreground p-2 bg-background rounded-lg">
                              <span>{t('store.forum.postsLabel')}</span>
                              <span className="font-bold text-foreground">1.491</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground p-2 bg-background rounded-lg">
                              <span>{t('store.forum.likesLabel')}</span>
                              <span className="font-bold text-foreground">1.560</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground p-2 bg-background rounded-lg">
                              <Clock className="w-3 h-3" />
                              <span className="text-xs">{formatRelativeTime(posts[0].createdAt)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Right Content - Post */}
                        <div className="lg:col-span-9 p-6 min-h-[400px] flex flex-col">
                          {/* Topic Original Badge */}
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-card-border">
                            <Badge className="bg-primary/20 text-primary border-primary/30 text-sm px-4 py-1.5">
                              ● TÓPICO ORIGINAL
                            </Badge>
                            {/* Author actions for own topic - Hidden if deleted by moderator */}
                            {player?.id === topic.authorId && !isPostDeletedByModerator(posts[0].content) && (
                              <TopicAuthorActions
                                topicId={topic.id}
                                firstPostId={posts[0].id}
                                isLocked={topic.isLocked}
                                onEditPost={() => handleEditPost(posts[0].id)}
                                onLock={handleLockTopic}
                                onUnlock={handleUnlockTopic}
                                onDelete={handleDeleteTopic}
                              />
                            )}
                          </div>

                          {/* Post Content */}
                          <div className="flex-1 mb-6">
                            {isPostDeletedByModerator(posts[0].content) ? (
                              // Deleted by moderator message
                              <div className="bg-destructive/10 border-2 border-destructive/30 rounded-lg p-6">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <ShieldAlert className="w-6 h-6 text-destructive" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-base font-bold text-destructive mb-1">
                                      {t('store.forum.postDeleted')}
                                    </h4>
                                    <p className="text-sm text-destructive/80">
                                      Removido por <span className="font-bold underline">{getModeratorNameFromDeletedPost(posts[0].content)}</span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // Normal post content
                              <div className="prose max-w-none">
                                <p className="text-foreground text-lg leading-relaxed whitespace-pre-wrap">
                                  {posts[0].content}
                                </p>
                              </div>
                            )}
                            
                            {/* Edited indicator */}
                            {posts[0].isEdited && posts[0].editedAt && !isPostDeletedByModerator(posts[0].content) && (
                              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-card-border/50">
                                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground italic">
                                  Essa postagem foi editada em {new Date(posts[0].editedAt).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })} às {new Date(posts[0].editedAt).toLocaleTimeString('pt-BR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Reactions & Share - Hidden for deleted posts */}
                          {!isPostDeletedByModerator(posts[0].content) && (
                            <div className="flex items-center justify-between pt-4 border-t border-card-border">
                              <PostReactions
                                postId={posts[0].id}
                                reactions={posts[0].reactions}
                                onReact={(type) => handleReact(posts[0].id, type)}
                                onRemoveReaction={() => handleRemoveReaction(posts[0].id)}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const url = window.location.href
                                  navigator.clipboard.writeText(url)
                                  toast.success(t('store.forum.linkCopied'))
                                }}
                                className="flex items-center gap-2 hover:bg-primary/10 hover:text-primary hover:border-primary transition-colors"
                              >
                                <Share2 className="w-4 h-4" />
                                <span className="font-medium">{t('store.forum.share')}</span>
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Caixa>
                )}

                {/* Anúncio da loja gratuita depois do texto do tópico (some quando não há). */}
                {posts.length > 0 && <EspacoDeAnuncio posicao="ARTICLE_END" className="my-6" />}

                {/* Community Responses Header */}
                {posts.length > 1 && (
                  <Caixa id="forum.topico.respostas.titulo">
                  <div className="flex items-center gap-3 py-6">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      <h3 className="text-lg font-bold text-foreground">
                        {t('store.forum.community', { count: posts.length - 1 })}
                      </h3>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent" />
                    <span className="text-sm text-muted-foreground">{t('store.forum.replies')}</span>
                  </div>
                  </Caixa>
                )}

                {/* Community Responses */}
                {posts.length > 1 && (
                  <Caixa id="forum.topico.respostas">
                  <div className="space-y-4">
                    {posts.slice(1).map((post) => (
                      <Caixa id="forum.topico.resposta" repetida key={post.id}>
                      <Card
                        key={post.id}
                        className="group border-card-border hover:border-primary/30 transition-all overflow-hidden"
                      >
                        <CardContent className="p-0">
                          {/* Grid Layout: Author Sidebar + Content */}
                          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                            
                            {/* Left Sidebar - Author Info */}
                            <div className="lg:col-span-3 bg-background-secondary border-r border-card-border p-6 flex flex-col">
                              {/* Avatar */}
                              <div className="flex justify-center mb-4">
                                <div className="w-16 h-16 rounded-xl overflow-hidden shadow-lg border-2 border-card-border">
                                  <img
                                    src={getAvatarUrl(post.authorHytaleNick || post.authorName, 128)}
                                    alt={post.authorName || 'Jogador'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement
                                      target.style.display = 'none'
                                      target.parentElement!.classList.add('bg-gradient-to-br', 'from-primary', 'via-accent', 'to-primary', 'flex', 'items-center', 'justify-center', 'text-primary-foreground', 'font-bold', 'text-2xl')
                                      target.parentElement!.textContent = post.authorName ? post.authorName[0].toUpperCase() : '?'
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Author Name */}
                              <div className="text-center mb-4">
                                <h3 className="text-base font-bold text-foreground mb-1">
                                  {post.authorName || 'Jogador'}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                  @{post.authorHytaleNick || 'jogador'}
                                </p>
                                {post.authorRole && (
                                  <p className="text-xs text-primary font-semibold mt-1">
                                    {post.authorRole}
                                  </p>
                                )}
                              </div>

                              {/* Badges */}
                              {post.authorBadges.length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-center mb-4 pb-4 border-b border-card-border">
                                  {post.authorBadges.map((badge) => (
                                    <AnimatedBadge
                                      key={badge.id}
                                      name={badge.name}
                                      icon={badge.icon}
                                      style={(badge.style as BadgeStyle) || 'DEFAULT'}
                                      animation={(badge.animation as BadgeAnimation) || 'NONE'}
                                      className="text-xs"
                                    />
                                  ))}
                                </div>
                              )}

                              {/* Post Meta */}
                              <div className="mt-auto">
                                <div className="flex items-center justify-center text-xs text-muted-foreground p-2 bg-background rounded-lg">
                                  <Clock className="w-3 h-3 mr-2" />
                                  <span>{formatRelativeTime(post.createdAt)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Right Content - Post */}
                            <div className="lg:col-span-9 p-6 min-h-[200px] flex flex-col">
                              {/* Header */}
                              <div className="flex items-center justify-between mb-4 pb-3 border-b border-card-border">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <MessageCircle className="w-4 h-4" />
                                  <span>{t('store.forum.reply')}</span>
                                </div>
                                {!isPostDeletedByModerator(post.content) && (
                                  <PostModTools
                                    postId={post.id}
                                    isOwnPost={player?.id === post.authorId}
                                    onEdit={() => handleEditPost(post.id)}
                                    onDelete={() => handleDeletePost(post.id)}
                                  />
                                )}
                              </div>

                              {/* Post Content */}
                              <div className="flex-1 mb-6">
                                {isPostDeletedByModerator(post.content) ? (
                                  // Deleted by moderator message
                                  <div className="bg-destructive/10 border-2 border-destructive/30 rounded-lg p-6">
                                    <div className="flex items-center gap-4">
                                      <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                                        <ShieldAlert className="w-6 h-6 text-destructive" />
                                      </div>
                                      <div className="flex-1">
                                        <h4 className="text-base font-bold text-destructive mb-1">
                                          {t('store.forum.postDeleted')}
                                        </h4>
                                        <p className="text-sm text-destructive/80">
                                          Removido por <span className="font-bold underline">{getModeratorNameFromDeletedPost(post.content)}</span>
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  // Normal post content
                                  <div className="prose max-w-none">
                                    <p className="text-foreground text-base leading-relaxed whitespace-pre-wrap">
                                      {post.content}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Edited indicator */}
                                {post.isEdited && post.editedAt && !isPostDeletedByModerator(post.content) && (
                                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-card-border/50">
                                    <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground italic">
                                      Essa postagem foi editada em {new Date(post.editedAt).toLocaleDateString('pt-BR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })} às {new Date(post.editedAt).toLocaleTimeString('pt-BR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Reactions & Share - Hidden for deleted posts */}
                              {!isPostDeletedByModerator(post.content) && (
                                <div className="flex items-center justify-between pt-4 border-t border-card-border">
                                  <PostReactions
                                    postId={post.id}
                                    reactions={post.reactions}
                                    onReact={(type) => handleReact(post.id, type)}
                                    onRemoveReaction={() => handleRemoveReaction(post.id)}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const url = window.location.href
                                      navigator.clipboard.writeText(url)
                                      toast.success(t('store.forum.linkCopied'))
                                    }}
                                    className="flex items-center gap-2 hover:bg-primary/10 hover:text-primary hover:border-primary transition-colors"
                                  >
                                    <Share2 className="w-4 h-4" />
                                    <span className="font-medium">{t('store.forum.share')}</span>
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      </Caixa>
                    ))}
                  </div>
                  </Caixa>
                )}
              </>
            )}

            {/* Reply Form */}
            {topic.isLocked ? (
              <Caixa id="forum.topico.trancado" miolo>
              <Card className="mt-8 border-warning/30 bg-warning/5 shadow-glow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-warning" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {t('store.forum.lockedTitle')}
                      </h3>
                      <p className="text-muted-foreground">
                        {t('store.forum.lockedBody')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Caixa>
            ) : (
          <Caixa id="forum.topico.responder" miolo>
          <Card className="mt-8 border-card-border shadow-glow">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                {t('store.forum.replyToTopic')}
              </h3>
              {!isAuthenticated ? (
                <div className="py-8 text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Lock className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-foreground">
                    {t('store.forum.authRequired')}
                  </h4>
                  <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
                    {t('store.forum.authRequiredBody')}
                  </p>
                  <Button
                    onClick={openLoginModal}
                    size="lg"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {t('store.forum.signIn')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleReply}>
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Escreva sua resposta aqui... Seja respeitoso e construtivo!"
                    className="w-full bg-background-secondary border border-border rounded-xl p-4 min-h-[160px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground resize-none transition-all"
                    required
                  />
                  <div className="flex justify-between items-center mt-4">
                    <p className="text-sm text-muted-foreground">
                      Mínimo de 10 caracteres
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting || !replyContent.trim() || replyContent.length < 10}
                      size="lg"
                      className="shadow-lg hover:shadow-glow transition-all"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Enviar Resposta
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
          </Caixa>
            )}
        </div>
      </div>

      {/* Modals */}
      <EditPostModal
        isOpen={editPostModalOpen}
        onClose={() => setEditPostModalOpen(false)}
        onSave={handleEditPostSave}
        initialContent={selectedPost?.content || ''}
        postId={selectedPost?.id || ''}
      />

      <MoveTopicModal
        isOpen={moveTopicModalOpen}
        onClose={() => setMoveTopicModalOpen(false)}
        onMove={handleMoveTopicConfirm}
        currentBoardId={topic?.boardId || ''}
        topicId={topic?.id || ''}
        storeId={store?.id || ''}
      />

      <DeleteConfirmModal
        isOpen={deleteTopicModalOpen}
        onClose={() => setDeleteTopicModalOpen(false)}
        onConfirm={confirmDeleteTopic}
        title="Deletar Tópico"
        message="Tem certeza que deseja deletar este tópico? Todos os posts dentro dele também serão removidos."
        confirmText="Deletar Tópico"
      />

      <DeleteConfirmModal
        isOpen={deletePostModalOpen}
        onClose={() => setDeletePostModalOpen(false)}
        onConfirm={confirmDeletePost}
        title="Deletar Post"
        message="Tem certeza que deseja deletar este post?"
        confirmText="Deletar Post"
      />
    </div>
  )
}
